import {ChangeEvent, FC, FormEventHandler, useCallback, useEffect, useMemo, useState} from 'react';
import {validateNameOrEmail} from '~/common/utils';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {PlusIcon} from '~library/svg/PlusIcon';
import {ErrorCircleIcon} from '~library/svg/v2/ErrorCircleIcon';
import {TrashIcon} from '~library/svg/v2/TrashIcon';
import {areAllValuesFalsy} from '~library/utils/utils';
import {link, tanzanite} from '~libSass/base/_exports.sass';
import {ConfirmRemoveSectionModal} from './ConfirmRemoveSectionModal';
import './DetailsFieldsOverlays.sass';

export type GuestOfHonor = {name: string; email: string};

interface OverlayGuestsOfHonorProps {
  isOpen: boolean;
  onClose: () => void;
  guestsOfHonor: GuestOfHonor[];
  onSubmit: (gohArrToSubmit: GuestOfHonor[]) => void;
}

export const OverlayGuestsOfHonor: FC<OverlayGuestsOfHonorProps> = ({
  isOpen,
  onClose,
  guestsOfHonor,
  onSubmit,
}) => {
  const [updatedGohArr, setUpdatedGohArr] = useState<GuestOfHonor[]>([...guestsOfHonor]);
  const [errors, setErrors] = useState<GuestOfHonor[]>([]);
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);
  const showAddMoreGohButton = updatedGohArr.length < 2;

  const hasGuestsOfHonor = useMemo(
    () => guestsOfHonor.some((goh: GuestOfHonor) => !areAllValuesFalsy(goh)),
    [guestsOfHonor]
  );

  useEffect(() => {
    setUpdatedGohArr([...guestsOfHonor]);
    setErrors([]);
  }, [guestsOfHonor]);

  const resetErrors = (index: number) => {
    setErrors((prevGohErrors) => {
      const latestErrors = [...prevGohErrors];

      latestErrors[index] = {name: '', email: ''};
      return latestErrors;
    });
  };

  const addAnotherGoh = useCallback(() => {
    if (updatedGohArr.length < 2) {
      setUpdatedGohArr([...updatedGohArr, {name: '', email: ''}]);
    }
  }, [updatedGohArr]);

  const removeThisGoh = (index: number) => {
    // clear out both the goh array & the array created for error tracking
    setUpdatedGohArr((prevGohArr) => prevGohArr.filter((_, i) => i !== index));
    setErrors((prevGohErrors) => prevGohErrors.filter((_, i) => i !== index));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onChange = useCallback((index: number, field: string, newValue: string) => {
    resetErrors(index);

    setUpdatedGohArr((prevGohArr) => {
      const updatedData = [...prevGohArr];

      updatedData[index] = {
        ...updatedData[index],
        [field]: newValue,
      };

      return updatedData;
    });
  }, []);

  const onConfirmRemoveSection = useCallback(() => {
    onSubmit([]);
    onClose();
  }, [onSubmit, onClose]);

  const onRemoveSection = useCallback(() => {
    setIsConfirmRemoveModalOpen(true);
  }, []);

  const onCloseConfirmRemoveModal = useCallback(() => {
    setIsConfirmRemoveModalOpen(false);
  }, []);

  const saveGoh: FormEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const gohArrToSubmit = [...updatedGohArr];
      const latestErrors: GuestOfHonor[] = Array.from({length: gohArrToSubmit.length}).fill({
        name: '',
        email: '',
      }) as GuestOfHonor[];
      let isValid = true;

      gohArrToSubmit.forEach((goh, i) => {
        const nameError = validateNameOrEmail(
          'name',
          goh.name,
          "Guest of Honor's name is required."
        );
        const emailError = validateNameOrEmail(
          'email',
          goh.email,
          'Valid email address is required.'
        );

        if (nameError || emailError) {
          latestErrors[i] = {name: nameError, email: emailError};
          isValid = false;
        }
      });

      if (isValid) {
        onSubmit(gohArrToSubmit);
        onClose();
      } else {
        setErrors(latestErrors);
      }
    },
    [onClose, onSubmit, updatedGohArr]
  );

  return (
    <ResponsivePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Guest of Honor"
      modalId="guest-of-honor"
      showCover
      renderMobileViewAs="bottom-sheet"
      disableOverlayClick
    >
      <form onSubmit={saveGoh} className="guest-of-honor__overlay-form">
        <div className="guest-of-honor__overlay-form-content">
          <div className="goh__disclaimer">
            <ErrorCircleIcon color={link} ratio={0.75} />
            <Typography variant="paragraph2" color="link" className="goh__disclaimer-heading">
              Psst... don&apos;t add the Guest of Honor if your event is a surprise!
            </Typography>
          </div>
          <Typography variant="paragraph2" color="asphalt">
            Your Guest of Honor&apos;s name will be featured in the event&apos;s details. Plus,
            they&apos;ll be added to the guest list and receive information emails about this event.
          </Typography>
          {updatedGohArr.map((goh, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: existing error
            <div className="event-details__overlay-box" key={index}>
              <div className="event-details__goh-label">
                <Typography variant="label2" as="label" size="small">
                  Guest of Honor Info
                </Typography>
                {index > 0 && (
                  <Button
                    variant="unstyled-button"
                    size="small"
                    onClick={() => removeThisGoh(index)}
                    data-qa-id="remove-additional-guest-of-honor"
                    className="goh__remove-button"
                  >
                    <TrashIcon color={tanzanite} ratio={0.5} />
                  </Button>
                )}
              </div>
              <Input
                id={`nameInput-${index}`}
                data-qa-id={`event-goh-overlay-name-${index}`}
                value={goh.name}
                error={(errors[index] && errors[index].name) || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(index, 'name', e.target.value)
                }
                placeholder="First and last name"
                autoFocus={index === 0}
                className="event-details__goh-name"
              />
              <Input
                data-qa-id={`event-goh-overlay-email-${index}`}
                error={(errors[index] && errors[index].email) || ''}
                value={goh.email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(index, 'email', e.target.value)
                }
                placeholder="Email address"
                className="event-details__goh-email"
              />
              {showAddMoreGohButton && (
                <Button
                  variant="link"
                  size="small"
                  onClick={addAnotherGoh}
                  id="addGOH"
                  data-qa-id="add-additional-guest-of-honor"
                  type="button"
                  className="goh__add-another-button"
                >
                  <PlusIcon ratio={0.5} color={tanzanite} />
                  Add Another Guest of Honor
                </Button>
              )}
            </div>
          ))}
        </div>
        <div className="create-flow__overlay-ctas">
          <Button
            id="guest-of-honor-submit"
            variant="primary"
            fullWidth
            size="large"
            data-qa-id="guest-of-honor__overlay-submit"
            type="submit"
            formSubmit
          >
            Save
          </Button>
          {hasGuestsOfHonor && (
            <Button
              id="guest-of-honor-remove-section"
              className="create-flow__overlay-remove-cta"
              variant="transparent"
              size="large"
              data-qa-id="guest-of-honor__overlay-remove-section"
              type="button"
              onClick={onRemoveSection}
            >
              Remove section
            </Button>
          )}
          {isConfirmRemoveModalOpen && (
            <ConfirmRemoveSectionModal
              isOpen
              onClose={onCloseConfirmRemoveModal}
              onConfirm={onConfirmRemoveSection}
              qaId="confirm-remove-guests-of-honor"
            />
          )}
        </div>
      </form>
    </ResponsivePanel>
  );
};
